import { Button, Card, MonoLabel } from '@/design-system';
import { formatMoney, type OrderFinancials } from '@/entities/order';
import { CardTitle } from '@/shared/ui';

import { dossierCopy } from '../copy';

export interface FinancialCardProps {
    financials: OrderFinancials;
    serviceLabel: string;
    /** Alleen wie facturen mag zien krijgt de knop. */
    onToBilling?: () => void;
}

/**
 * Omzet en marge van deze keuring.
 *
 * **Alleen voor `owner`.** De API laat deze velden voor een inspecteur weg, en
 * dan staat dit blok er niet - ook niet leeg.
 *
 * Dit zijn opbrengsten, geen herstelkosten: gebruik hier nooit de rood/amber
 * kleuren van de kostenblokken (patterns §3).
 *
 * ## Over deze prijs
 *
 * Onder de bedragen staat in gewone taal wat de prijs van een keuring wel en
 * niet is: hij hoort bij déze keuring, hij is vastgelegd toen de keuring werd
 * aangenomen, het standaardtarief verander je ergens anders, en hij rekent niet
 * mee met ingevulde aantallen of m². Dat is geen overbodige uitleg maar het
 * antwoord op de vraag die iedereen stelt zodra hij een bedrag ziet - en het
 * hoort op de plek van het bedrag te staan, niet in een handleiding.
 */
export function FinancialCard({ financials, serviceLabel, onToBilling }: FinancialCardProps) {
    return (
        <Card padding="md">
            <CardTitle className="mb-3">{dossierCopy.financeTitle}</CardTitle>

            <div className="mb-2 flex justify-between gap-3 text-[13px]">
                <span className="text-[var(--text-tertiary-safe)]">{serviceLabel}</span>
                <span className="text-[var(--text-primary)]">
                    {formatMoney(financials.price, { cents: true })}
                </span>
            </div>
            <div className="mb-2 flex justify-between gap-3 text-[13px]">
                <span className="text-[var(--text-tertiary-safe)]">{dossierCopy.financeFee}</span>
                <span className="text-[var(--text-primary)]">
                    {formatMoney(financials.inspectorFee, { cents: true })}
                </span>
            </div>
            <div className="flex justify-between gap-3 border-t border-[var(--border-subtle)] pt-[9px] text-[14px]">
                <span className="font-medium text-[var(--text-primary)]">
                    {dossierCopy.financeMargin}
                </span>
                <span className="font-[family-name:var(--font-display)] font-semibold text-[var(--text-primary)]">
                    {formatMoney(financials.margin, { cents: true })}
                </span>
            </div>
            <p className="m-0 mt-2 text-[12px] text-[var(--text-tertiary-safe)]">
                {dossierCopy.financeCredits(financials.creditsReserved, financials.creditsConsumed)}
            </p>

            <section
                aria-label={dossierCopy.financeAboutPrice}
                className="mt-3 border-t border-[var(--border-subtle)] pt-[10px]"
            >
                <MonoLabel size={10}>{dossierCopy.financeAboutPrice}</MonoLabel>
                <p className="m-0 mt-1 text-[12px] leading-[1.5] text-[var(--text-secondary)]">
                    {dossierCopy.financeOnlyThisOrder}
                </p>
                <p className="m-0 mt-[6px] text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    {dossierCopy.financeTariffElsewhere}
                </p>
                <p className="m-0 mt-[6px] text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    {dossierCopy.financeNoAutoPrice}
                </p>
                {onToBilling ? (
                    <p className="m-0 mt-[6px] text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                        {dossierCopy.financeInvoiceEditable}
                    </p>
                ) : null}
            </section>

            {onToBilling ? (
                <Button
                    variant="outline"
                    size="md"
                    fullWidth
                    className="mt-[14px]"
                    onClick={onToBilling}
                >
                    {dossierCopy.financeToBilling}
                </Button>
            ) : null}
        </Card>
    );
}
